import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, Shield, Drama, Clock, CalendarClock, BookOpen } from "lucide-react";
import { letterMilestone } from "@/components/LetterProgressBar";
import { statusTitle } from "@/components/LetterStatusCard";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import { fmtDate, fmtTime } from "@/components/PageHeader";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortNo } from "@/lib/caseNo";

// Badge sizing by how many are shown: 1 = large, 2 = medium, 3 = small (stacked on phones so all fit one row).
const SIZES = {
  1: { grid: "grid-cols-1 sm:max-w-md", card: "flex-row gap-4 p-5", tile: "h-14 w-14", icon: "h-7 w-7", value: "text-3xl", detail: true },
  2: { grid: "grid-cols-2 lg:max-w-3xl", card: "flex-col gap-2 p-3 text-center sm:flex-row sm:gap-4 sm:p-4 sm:text-left", tile: "h-11 w-11 sm:h-12 sm:w-12", icon: "h-6 w-6", value: "text-xl sm:text-2xl", detail: true },
  3: { grid: "grid-cols-3", card: "flex-col gap-1.5 p-2.5 text-center sm:flex-row sm:gap-3 sm:p-3 sm:text-left", tile: "h-9 w-9 sm:h-10 sm:w-10", icon: "h-5 w-5", value: "text-base sm:text-xl", detail: false },
};
const TILE = { merits: "bg-sky-100 text-sky-600 ring-sky-200", lettering: "bg-violet-100 text-violet-700 ring-violet-200", thespian: "bg-amber-100 text-amber-700 ring-amber-200", rally: "bg-emerald-100 text-emerald-700 ring-emerald-200" };
const ICON = { merits: Scale, lettering: Shield, thespian: Drama, rally: BookOpen };
const GLOW = { merits: "14 165 233", lettering: "139 92 246", thespian: "245 158 11", rally: "16 185 129" };
const CHIP = { merits: "bg-sky-600", lettering: "bg-violet-600", thespian: "bg-amber-500", rally: "bg-emerald-600" };
const PULSE_MS = 3200;
const signed = (n, unit = "") => `${n > 0 ? "+" : "−"}${fmtPts(Math.abs(n))}${unit}`;

// Remembers each badge's last value per member (this device); returns {kind: chipText} for values that changed.
export function useBadgePulse(seenKey, current) {
  const [pulse, setPulse] = useState({});
  const checked = useRef({});
  const userId = seenKey;
  const key = `rallypointgo_badge_seen_${seenKey}`;
  const sig = JSON.stringify(current);
  useEffect(() => {
    if (!userId) return;
    let seen = {};
    try { seen = JSON.parse(localStorage.getItem(key) || "{}"); } catch { seen = {}; }
    const fresh = {};
    Object.entries(current).forEach(([kind, cur]) => {
      if (cur == null || checked.current[kind]) return;
      checked.current[kind] = true;
      const prev = seen[kind];
      seen[kind] = cur;
      if (prev == null) return;
      if (kind === "thespian") {
        if (prev.rank !== cur.rank) fresh[kind] = cur.rank ? "New rank" : "Rank changed";
        else if (prev.pts !== cur.pts) fresh[kind] = signed(cur.pts - prev.pts, " pts");
      } else if (prev !== cur && kind === "rally") {
        fresh[kind] = `${cur > prev ? "+" : "−"}$${Math.abs(cur - prev).toFixed(2)}`;
      } else if (prev !== cur) {
        fresh[kind] = signed(cur - prev, kind === "lettering" ? " pts" : "");
      }
    });
    localStorage.setItem(key, JSON.stringify(seen));
    if (Object.keys(fresh).length) {
      setPulse((p) => ({ ...p, ...fresh }));
      setTimeout(() => setPulse((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !(k in fresh)))), PULSE_MS);
    }
  }, [userId, sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return pulse;
}

function StatBadge({ kind, to, size, label, labelTitle, value, valueTestId, detail, corners, testId, delay, pulse }) {
  const Icon = ICON[kind];
  return (
    <Link to={to} className={`relative block min-w-0 rounded-xl ${pulse ? "badge-glow" : ""}`} style={{ "--glow": GLOW[kind] }}
      data-testid={testId} data-pulse={pulse ? "true" : undefined}>
      {pulse && (
        <span className={`badge-chip pointer-events-none absolute -top-2 right-2 z-10 rounded-full px-2 py-0.5 text-[10px] font-bold text-white shadow ${CHIP[kind]}`} data-testid={`${testId}-change-chip`}>{pulse}</span>
      )}
      <div style={{ animationDelay: `${delay}ms` }}
        className={`flex h-full items-center rounded-xl border border-slate-200 bg-white shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md animate-fade-in-up ${size.card}`}>
        <span className={`relative flex shrink-0 items-center justify-center rounded-xl ring-1 ${TILE[kind]} ${size.tile}`}>
          <Icon className={size.icon} />
          {corners}
        </span>
        <div className="w-full min-w-0 sm:w-auto sm:flex-1">
          <p className="truncate text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs" title={labelTitle || label}>{label}</p>
          <p className={`font-heading font-extrabold leading-tight text-slate-900 tabular-nums line-clamp-2 ${size.value}`} data-testid={valueTestId}>{value}</p>
          {size.detail && detail}
        </div>
      </div>
    </Link>
  );
}

function MeritCorners({ d, ackTo, tp }) {
  const navigate = useNavigate();
  const awaiting = (d?.merits_awaiting_ack || 0) + (d?.demerits_awaiting_ack || 0);
  const confs = d?.scheduled_conferences || [];
  const clock = "absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white shadow";
  const go = (e) => { e.preventDefault(); e.stopPropagation(); navigate(ackTo); };
  return (
    <>
      {awaiting > 0 && (ackTo ? (
        <span role="button" tabIndex={0} title={`${awaiting} awaiting your acknowledgement — tap to review`}
          onClick={go} onKeyDown={(e) => e.key === "Enter" && go(e)}
          className={`${clock} cursor-pointer transition-transform hover:scale-110`} data-testid={`${tp}stat-demerits-awaiting`}>
          <Clock className="h-3 w-3" />
        </span>
      ) : (
        <span className={clock} title={`${awaiting} awaiting acknowledgement`} data-testid={`${tp}stat-demerits-awaiting`}><Clock className="h-3 w-3" /></span>
      ))}
      {d?.conferences_scheduled > 0 && (
        <span className="absolute -left-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shadow"
          title={"Conference scheduled:\n" + confs.map((c) => `${fmtDate(c.conference_date)}${c.conference_time ? ` at ${fmtTime(c.conference_time)}` : ""} · ${shortNo(c.case_number)}`).join("\n")}
          data-testid={`${tp}stat-demerits-conference`}>
          <CalendarClock className="h-3 w-3" />
        </span>
      )}
    </>
  );
}

const shortStatus = (ls) => !ls?.first_letter_earned ? "Toward letter" : ls.additional_awards ? `Letter + ${ls.additional_awards}` : "Letter earned";

function ThinBar({ pct, color, testId }) {
  return <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100" data-testid={testId}><div className={`h-full rounded-full ${color} transition-[width] duration-700`} style={{ width: `${pct}%` }} /></div>;
}

// Compact Merits / Lettering / Thespian badge row (member + parent student dashboards). tp = test-id prefix.
export function StatBadgeRow({ d, th, meritOn, letteringOn, thespianOn, links, seenKey, ackTo, tp = "" }) {
  const ls = d?.letter_status;
  const m = letterMilestone(ls);
  const showTh = thespianOn && th?.enabled && th.approved_count > 0;
  const badgeCount = [meritOn, letteringOn, showTh].filter(Boolean).length;
  const pulse = useBadgePulse(seenKey, {
    merits: meritOn && d ? d.net_score ?? null : null,
    lettering: letteringOn && d ? (ls?.total_approved_points ?? d.approved_lettering_points ?? null) : null,
    thespian: showTh ? { rank: th.rank || "", pts: th.total_points || 0 } : null,
  });
  const badges = [
    meritOn && { kind: "merits", to: links.merits, testId: `${tp}merit-summary-widget`, label: "Net merits", value: d?.net_score ?? "—", valueTestId: `${tp}stat-net`,
      corners: <MeritCorners d={d} ackTo={ackTo} tp={tp} />,
      detail: <p className="mt-0.5 text-xs font-semibold tabular-nums" data-testid={`${tp}stat-merit-split`}><span className="text-emerald-600">+{d?.total_merits ?? 0}</span><span className="text-slate-300"> / </span><span className="text-red-600">−{d?.total_demerits ?? 0}</span></p> },
    letteringOn && { kind: "lettering", to: links.lettering, testId: `${tp}stat-lettering-points`, label: !ls ? "Lettering" : badgeCount === 3 ? shortStatus(ls)
        : <><span className="sm:hidden">{shortStatus(ls)}</span><span className="hidden sm:inline">{statusTitle(ls)}</span></>,
      labelTitle: ls ? statusTitle(ls) : "Lettering",
      value: `${ls?.total_approved_points ?? d?.approved_lettering_points ?? 0} pts`, valueTestId: `${tp}dash-letter-total-points`,
      detail: m && <ThinBar pct={m.done ? 100 : Math.min(100, Math.round((m.have / Math.max(m.need, 1)) * 100))} color="bg-violet-500" testId={`${tp}dash-letter-bar`} /> },
    showTh && { kind: "thespian", to: links.thespian, testId: `${tp}thespian-dashboard-card`, label: "ITS rank",
      value: th.rank || `${fmtPts(th.total_points)} pts`, valueTestId: `${tp}thespian-rank-name`,
      detail: <p className="mt-0.5 text-xs text-slate-500" data-testid={`${tp}thespian-next-rank`}>{th.next_rank ? <><span className="font-semibold tabular-nums text-slate-700">{fmtPts(th.points_to_next)}</span> pts to {th.next_rank}</> : "Highest rank reached"}</p> },
  ].filter(Boolean);
  if (!badges.length) return null;
  const size = SIZES[badges.length];
  return (
    <div className={`grid gap-2 sm:gap-3 ${size.grid}`} data-testid={`${tp}member-stat-badges`} data-count={badges.length}>
      {badges.map((b, i) => <StatBadge key={b.kind} {...b} size={size} delay={i * 70} pulse={pulse[b.kind]} />)}
    </div>
  );
}

// Tiny badge for list cards (parent "My Students"): icon + value + label, glows with a change chip.
// stacked = icon above text (3–4 across); onClick makes it a shortcut (stops the card's own click).
export function MiniBadge({ kind, label, value, pulse, testId, onClick, stacked }) {
  const Icon = ICON[kind];
  const click = onClick && ((e) => { e.stopPropagation(); onClick(); });
  return (
    <div role={onClick ? "link" : undefined} tabIndex={onClick ? 0 : undefined} onClick={click} onKeyDown={click && ((e) => e.key === "Enter" && click(e))}
      className={`relative flex min-w-0 rounded-lg border border-slate-100 bg-slate-50 ${stacked ? "flex-col items-center gap-1 px-1 py-2 text-center" : "items-center gap-2 px-2.5 py-2"} ${onClick ? "cursor-pointer transition-colors hover:border-slate-300 hover:bg-white" : ""} ${pulse ? "badge-glow" : ""}`}
      style={{ "--glow": GLOW[kind] }} data-testid={testId} data-pulse={pulse ? "true" : undefined}>
      {pulse && <span className={`badge-chip pointer-events-none absolute -top-2 right-1 z-10 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold text-white shadow ${CHIP[kind]}`} data-testid={`${testId}-change-chip`}>{pulse}</span>}
      <span className={`flex shrink-0 items-center justify-center rounded-lg ring-1 ${TILE[kind]} ${stacked ? "h-7 w-7" : "h-8 w-8"}`}><Icon className="h-4 w-4" /></span>
      <div className="w-full min-w-0 leading-tight">
        <p className={`font-heading font-extrabold tabular-nums text-slate-900 ${stacked ? "line-clamp-2 text-sm" : "truncate text-lg"}`} data-testid={`${testId}-value`}>{value}</p>
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      </div>
    </div>
  );
}

// Team member dashboard wrapper.
export default function MemberStatBadges({ d, meritOn, letteringOn, thespianOn }) {
  const [th, setTh] = useState(null);
  const { user } = useAuth();
  useEffect(() => { if (thespianOn) api.get("/thespian/my").then(({ data }) => setTh(data)).catch(() => {}); }, [thespianOn]);
  return (
    <StatBadgeRow d={d} th={th} meritOn={meritOn} letteringOn={letteringOn} thespianOn={thespianOn}
      links={{ merits: "/member/merits", lettering: "/member/lettering", thespian: "/member/thespian" }}
      seenKey={user?.id} ackTo="/member/merits?ack=1" />
  );
}
